import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import { flatten, deepen } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import { parse } from '../../versa/misc.js';
import view from '../views/products.js';
import form from '../forms/products.js';
import * as types from '../types.js';

const products = function()
{

	let products_crud = crud.create({
		async addFilter(params) {
			await crud.addFilter.call(this, params);
			if (this.filter && JSON.stringify(this.filter).includes('is_disabled'))
				params.append('include_disabled', '1');
		},
		editFilter() {
			filter.edit(this.applyFilter.bind(this), this.filter);
		},
		async applyFilter(data) {
			try {
				this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
				this.loadPage(1);
			} catch (e) {
				notify(e?.message || 'Erro ao aplicar filtro.', 'Filtro');
				console.error('applyFilter', e);
			}
		},
		insertRecord()
		{
			this.editRecord({ gtin: 'SEM GTIN' });
		},
		async loadBaseData()
		{
			await Promise.all([
				api.call(api.list_companies, this.queryAll()).then((data) => data && db.set('companies', data).catch(error => notify(error))),
				api.call(api.list_classes, this.queryAll()).then((data) => data && db.set('classes', data).catch(error => notify(error))),
				api.call(api.list_categories, this.queryAll()).then((data) => data && db.set('categories', data).catch(error => notify(error))),
				api.call(api.list_units, this.queryAll()).then((data) => data && db.set('units', data).catch(error => notify(error))),
				api.call(api.list_manufacturers, this.queryAll()).then((data) => data && db.set('manufacturers', data).catch(error => notify(error))),
				api.call(api.list_programs, this.queryAll()).then((data) => data && db.set('programs', data).catch(error => notify(error))),
				api.call(api.list_products, this.queryAll()).then((data) => data && db.set('products', data).catch(error => notify(error)))
			]);
		},
		async queryData(query)
		{
			return await api.call(api.list_products, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_product, ...JSON.parse(id));
			_record.is_st = _record.taxation?.includes('S')
			_record.is_exempt = _record.taxation?.includes('I');
			_record.is_mono = _record.taxation?.includes('M');
			_record.classes = _record.classes?.map(entry => [_record.company_id, entry]);
			return flatten(_record, 'drug');
		},
		async setDisabled(disabled) {
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length) return notify('Selecione ao menos um produto.', 'Produtos');
			const action = disabled ? 'Inativar' : 'Ativar';
			ask(`Confirma ${action} ${ids.length} produto(s)?`, action, async (answer) => {
				if (answer !== 'Y') return;
				for (const [company_id, id] of ids) {
					const resp = await api.call(api.patch_product, company_id, id, { is_disabled: !!disabled });
					if (resp == null) { notify('Falha ao atualizar produto.', 'Produtos'); return; }
				}
				this.loadPage();
			});
		},
		async saveRecord(data, old_data)
		{
			let _record = {
				category_id: Number(data.category_id),
				purchasing_unit_id: Number(data.purchasing_unit_id),
				selling_unit_id: Number(data.selling_unit_id),
				manufacturer_id: Number(data.manufacturer_id),
				sku: [data.sku],
				taxons: [data.taxons],
				name: data.name,
				synonyms: data.synonyms,
				description: data.description,
				reference: data.reference,
				codigo_cliente: (data.codigo_cliente && data.codigo_cliente.trim()) ? data.codigo_cliente.trim() : null,
				program_id: (data.program_id != null && data.program_id !== '' && Number(data.program_id) >= 0) ? Number(data.program_id) : null,
				gtin: (data.gtin && data.gtin.trim()) ? data.gtin.trim() : 'SEM GTIN',
				ncm: data.ncm,
				cest: data.cest,
				origin: data.origin,
				taxation: (data.is_st == '1' ? 'S' : '') + (data.is_exempt == '1' ? 'I' : '') + (data.is_mono == '1' ? 'M' : ''),
				icms: Number(data.icms),
				shelf_life: Number(data.shelf_life),
				minimum_quantity: Number(data.minimum_quantity),
				quantity: Number(data.quantity),
				value: Number(data.value),
				is_disabled: data.is_disabled == '1',
				is_portaria_344: data.is_portaria_344 == '1',
				portaria_344_list: (data.portaria_344_list && data.portaria_344_list.trim()) ? data.portaria_344_list.trim() : null,
				c_benef: (data.c_benef && String(data.c_benef).trim()) ? String(data.c_benef).trim() : null,
				secondary_weight: Number(data.secondary_weight),
				secondary_height: Number(data.secondary_height),
				secondary_width: Number(data.secondary_width),
				secondary_length: Number(data.secondary_length),
				master_weight: Number(data.master_weight),
				master_height: Number(data.master_height),
				master_width: Number(data.master_width),
				master_length: Number(data.master_length),
				image: data.image,
				classes: parse(data.classes)?.map(entry => Number(entry[1]))
			};
			if (data.drug_anvisa)
				_record['drug'] = {
					anvisa: data.drug_anvisa,
					exemption: data.drug_exemption,
					max_price: Number(data.drug_max_price)
				};
			// Insert: old_data = { gtin: 'SEM GTIN' } (sem id). Update: old_data veio do loadRecord (tem id).
			const isUpdate = old_data && old_data.id != null && old_data.id !== '';
			if (isUpdate)
				return await api.call(api.update_product, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_product, data.company_id, _record);
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Posição', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do produto' },
		{ index: 3, field: 'category_id', label: 'Categoria', types: [types.datalist()], placeholder: '', title: 'Categoria do produto', store: 'categories' },
		{ index: 6, field: 'manufacturer_id', label: 'Fabricante', types: [types.datalist()], placeholder: '', title: 'Fabricante do produto', store: 'manufacturers' },
		{ index: 7, field: 'sku', label: 'SKU', types: [types.sku(12)], placeholder: '', title: 'Código do produto. Tamanho definido pela categoria. Use apenas A–N, P–Z e 0–9 (sem a letra O).' },
		{ index: 9, field: 'name', label: 'Nome', types: [types.name()], placeholder: '', title: 'Nome do produto' },
		{ index: 10, field: 'synonyms', label: 'Sinônimos', types: [types.array()], placeholder: '', title: 'Nomes alternativos do produto' },
		{ index: 11, field: 'description', label: 'Descrição', types: [], placeholder: '', title: 'Descrição do produto' },
		{ index: 12, field: 'reference', label: 'Referências', types: [types.array()], placeholder: '', title: 'Códigos de referência externos do produto' },
		{ index: 13, field: 'codigo_cliente', label: 'Cód. cliente', types: [types.name()], placeholder: '', title: 'Código do produto na base do cliente (DE/PARA)' },
		{ index: 14, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa do produto (estoque por programa)', store: 'programs', labels: ['name'] },
		{ index: 15, field: 'gtin', label: 'GTIN', types: [types.gtin()], placeholder: '', title: 'Número do código de barras ou "SEM GTIN"' },
		{ index: 16, field: 'ncm', label: 'NCM', types: [types.ncm()], placeholder: '00000000', title: 'Nomenclatura Comum do Mercosul' },
		{ index: 17, field: 'cest', label: 'CEST', types: [types.ncm()], placeholder: '00.000.00', title: 'Código Especificador da Substituição Tributária' },
		{ index: 18, field: 'origin', label: 'Origem', types: [types.opt_origin(), types.datalist()], placeholder: '', title: 'Origem do produto' },
		{ index: 19, field: 'taxation', label: 'Taxação', types: [], placeholder: '', title: 'Indicadores de taxação do produto' },
		{ index: 20, field: 'icms', label: 'ICMS', types: [types.number(2)], placeholder: '0,00', title: 'Alíquota específica de ICMS do produto, se houver' },
		{ index: 21, field: 'shelf_life', label: 'Vida', types: [types.number(0, 2)], placeholder: '0', title: 'Prazo de validade mínimo do produto em dias' },
		{ index: 22, field: 'minimum_quantity', label: 'Estoque Mín', types: [types.number(0, 2)], placeholder: '0', title: 'Estoque mínimo desejado do produto em unidades' },
		{ index: 23, field: 'quantity', label: 'Estoque', types: [types.number(0, 2)], placeholder: '0', title: 'Saldo agregado no cadastro (products.quantity). Não gera estoque WMS por posição; para carga inicial use entrada ao armazém.' },
		{ index: 24, field: 'value', label: 'Valor', types: [types.number(2)], placeholder: '0,00', title: 'Valor atual do estoque do produto' },
		{ index: 4, field: 'purchasing_unit_id', label: 'Un Compra', types: [types.datalist()], placeholder: '', title: 'Unidade de compra', store: 'units' },
		{ index: 5, field: 'selling_unit_id', label: 'Un Venda', types: [types.datalist()], placeholder: '', title: 'Unidade de venda', store: 'units' },
		//{ index: 32, field: 'anvisa', label: 'Anvisa', types: [], placeholder: '', title: 'Número de registro na Anvisa ou "ISENTO"' },
		//{ index: 33, field: 'exemption', label: 'Justificativa', types: [], placeholder: '', title: 'Justificativa caso seja "ISENTO" de registro na Anvisa' },
		//{ index: 34, field: 'max_price', label: 'PMC', types: [types.number(2)], placeholder: '0,00', title: 'Preço Máximo ao Consumidor' },
		{ index: 25, field: 'secondary_weight', label: 'ES Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso da embalagem secundária do produto em quilos' },
		{ index: 26, field: 'secondary_height', label: 'ES Altura', types: [types.number(3)], placeholder: '0,000', title: 'Altura da embalagem secundária do produto em metros' },
		{ index: 27, field: 'secondary_width', label: 'ES Largura', types: [types.number(3)], placeholder: '0,000', title: 'Largura da embalagem secundária do produto em metros' },
		{ index: 28, field: 'secondary_length', label: 'ES Compr', types: [types.number(3)], placeholder: '0,000', title: 'Comprimento da embalagem secundária do produto em metros' },
		{ index: 29, field: 'master_weight', label: 'ET Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso da embalagem terciária do produto em quilos (campo master_weight)' },
		{ index: 30, field: 'master_height', label: 'ET Altura', types: [types.number(3)], placeholder: '0,000', title: 'Altura da embalagem terciária do produto em metros (campo master_height)' },
		{ index: 31, field: 'master_width', label: 'ET Largura', types: [types.number(3)], placeholder: '0,000', title: 'Largura da embalagem terciária do produto em metros (campo master_width)' },
		{ index: 32, field: 'master_length', label: 'ET Compr', types: [types.number(3)], placeholder: '0,000', title: 'Comprimento da embalagem terciária do produto em metros (campo master_length)' },
		{ index: 33, field: 'image', label: 'Imagem', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Indicador de imagem associada ao produto' },
		{ index: 35, field: 'is_portaria_344', label: 'Portaria 344', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Produto sujeito à Portaria 344/98 (lista em portaria_344_list)' },
		{ index: 34, field: 'is_disabled', label: 'Inativo', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Produto inativo (não listado para entrada)' },
	];

	let operations = [
		{ hash: '#product-disable', label: 'Inativar' },
		{ hash: '#product-enable', label: 'Ativar' },
	];

	router.set('#product-disable', products_crud.setDisabled.bind(products_crud, true));
	router.set('#product-enable', products_crud.setDisabled.bind(products_crud, false));

	products_crud.run(
		'products',
		'Produtos',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		[]
	);

};

export default products;

